/**
 * FOCUS MODE CONTEXT
 * Phase 4 of "Executive Aesthetics & Flow" Strategy
 *
 * When the user enters "Training Mode" or "Deep Chat":
 * - Dim the rest of the interface
 * - Emphasize the active surface (handled by consumers via z-index)
 * - Provide ESC / X to exit
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { X, AppIcon } from '@/components/ui/icon';
import { MOTION_TRANSITIONS, MOTION_VARIANTS } from '@/lib/motion-primitives';
import { AnimatePresence, m } from '@/lib/motion-runtime';
import { cn } from '@/lib/utils';
import { iOS_SPRINGS } from '@/lib/animation-constants';
import { useTranslation } from 'react-i18next';

export type FocusModeType = 'training' | 'chat' | 'breathing' | null;

interface FocusModeContextType {
  isActive: boolean;
  mode: FocusModeType;
  enterFocusMode: (mode: Exclude<FocusModeType, null>) => void;
  exitFocusMode: () => void;
  toggleFocusMode: (mode: Exclude<FocusModeType, null>) => void;
}

const FocusModeContext = createContext<FocusModeContextType | undefined>(undefined);

interface FocusModeProviderProps {
  children: ReactNode;
}

export function FocusModeProvider({ children }: FocusModeProviderProps) {
  const [mode, setMode] = useState<FocusModeType>(null);
  const isActive = mode !== null;

  const enterFocusMode = useCallback((newMode: Exclude<FocusModeType, null>) => {
    setMode(newMode);
    document.body.style.overflow = 'hidden';
  }, []);

  const exitFocusMode = useCallback(() => {
    setMode(null);
    document.body.style.overflow = '';
  }, []);

  const toggleFocusMode = useCallback(
    (targetMode: Exclude<FocusModeType, null>) => {
      if (mode === targetMode) {
        exitFocusMode();
      } else {
        enterFocusMode(targetMode);
      }
    },
    [mode, enterFocusMode, exitFocusMode],
  );

  // Handle Escape key to exit focus mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        exitFocusMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, exitFocusMode]);

  // Cleanup overflow on unmount
  useEffect(
    () => () => {
      document.body.style.overflow = '';
    },
    [],
  );

  return (
    <FocusModeContext.Provider
      value={{ isActive, mode, enterFocusMode, exitFocusMode, toggleFocusMode }}
    >
      {children}
      <FocusModeBackdrop />
    </FocusModeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFocusMode(): FocusModeContextType {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }
  return context;
}

const FocusModeBackdrop = () => {
  const { isActive, mode, exitFocusMode } = useFocusMode();
  const { t } = useTranslation();

  const modeLabel = mode
    ? t(`focusMode.${mode}`, `${mode.charAt(0).toUpperCase()}${mode.slice(1)} Mode`)
    : '';

  return (
    <AnimatePresence>
      {isActive && (
        <m.div
          className={cn('fixed inset-0 z-[80] pointer-events-none', 'bg-transparent')}
          variants={MOTION_VARIANTS.fade}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={MOTION_TRANSITIONS.fade}
        >
          {/* Dimmed background */}
          <div className="absolute inset-0 bg-background/90 backdrop-frost-xl pointer-events-none" />

          {/* Subtle vignette */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.35) 100%)',
            }}
          />

          {/* Exit Button */}
          <m.button
            className={cn(
              'absolute top-4 right-4 z-[90]',
              'app-floating-surface inline-flex size-11 items-center justify-center',
              'text-muted-foreground hover:text-foreground',
              'transition-colors duration-200',
              'pointer-events-auto',
            )}
            onClick={exitFocusMode}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={iOS_SPRINGS.snappy}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={t('focusMode.exitHint', 'ESC to exit')}
          >
            <AppIcon icon={X} size="md" />
          </m.button>

          {/* Mode indicator */}
          {mode && (
            <m.div
              className="absolute top-4 left-4 z-[90] pointer-events-none"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={iOS_SPRINGS.snappy}
            >
              <div className="app-floating-surface flex items-center gap-2 px-3 py-1.5 text-primary">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-meta font-medium text-primary">{modeLabel}</span>
              </div>
            </m.div>
          )}

          {/* ESC hint */}
          <m.div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[90] pointer-events-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.5 }}
          >
            <kbd className="px-2 py-1 text-[10px] rounded border border-border/50 bg-muted/50 text-muted-foreground">
              {t('focusMode.exitHint', 'ESC to exit')}
            </kbd>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};
